import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function Footer() {
  const [activeModal, setActiveModal] = useState(null); // 'terms', 'returns', 'privacy', 'shipping'
  const [email, setEmail] = useState("");

  const closeModal = () => setActiveModal(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${SERVER_URL}/api/subscribe`, { email });
      toast.success("Thanks for subscribing!");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.message || "An error occurred. Please try again.");
      console.error(err);
    }
  };

  const modalContent = {
    terms: {
      title: "Terms of Service",
      content: (
        <>
          <p className="mb-4">
            Welcome to <strong className="text-gray-900">Pak Fashions</strong> — your trusted fashion
            destination in Nanyuki. By using our platform, you agree to the
            following terms and conditions. We reserve the right to update these
            terms without prior notice.
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>All items are intended for personal use only unless otherwise stated.</li>
            <li>Reselling of our products without prior written consent is strictly prohibited.</li>
            <li>All orders are subject to stock availability and confirmation of the order price.</li>
            <li>Prices and product descriptions may be updated periodically without notification.</li>
            <li>By placing an order, you confirm that all details provided are accurate and complete.</li>
          </ul>
        </>
      ),
    },
    returns: {
      title: "Return & Exchange Policy",
      content: (
        <>
          <p className="mb-4">
            At <strong className="text-gray-900">Pak Fashions</strong>, we want you to be fully satisfied
            with your purchase. However, if you are not completely happy with an
            item, we accept returns under the following conditions:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>Returns must be initiated within <strong className="text-gray-900">7 days</strong> of delivery.</li>
            <li>Items must be unused, unwashed, and in their original condition and packaging.</li>
            <li>Defective or damaged items may be returned or exchanged at no extra cost.</li>
            <li>Customized or clearance items are <strong className="text-gray-900">non-returnable</strong>.</li>
            <li>Shipping fees are non-refundable unless the error was on our end.</li>
          </ul>
          <p className="mt-4">
            For assistance, please contact our team via WhatsApp or email. We’re
            based in Nanyuki and can assist with quick local exchanges when necessary.
          </p>
        </>
      ),
    },
    privacy: {
      title: "Privacy Policy",
      content: (
        <>
          <p className="mb-4">
            Your privacy is very important to us. We are committed to protecting
            the personal information you share with us.
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>We collect minimal information needed to process orders and communicate updates.</li>
            <li>Your contact information is never sold or shared with third parties.</li>
            <li>Payment details are securely handled through trusted payment gateways.</li>
            <li>You may request to have your account or data permanently deleted at any time.</li>
            <li>Our site uses cookies to improve user experience and analytics.</li>
          </ul>
          <p className="mt-4">
            For privacy-related questions, contact us at{" "}
            <strong className="text-gray-900">privacy@pakfashion.com</strong>.
          </p>
        </>
      ),
    },
    shipping: {
      title: "Shipping Information",
      content: (
        <>
          <p className="mb-4">
            We offer fast, affordable, and reliable shipping across Kenya.
            Orders are typically processed within <strong className="text-gray-900">24–48 hours</strong>.
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li><strong className="text-gray-900">Free delivery within Nanyuki town</strong> and surrounding areas.</li>
            <li>Orders outside Nanyuki are delivered within 2–5 business days via trusted courier services.</li>
            <li>Tracking information is shared via email or SMS once your order is dispatched.</li>
            <li>Shipping is free for orders above <strong className="text-gray-900">Ksh 10,000</strong> nationwide.</li>
            <li>Same-day delivery may be available within Nanyuki for orders placed before 1 PM.</li>
          </ul>
          <p className="mt-4">
            If you have specific delivery needs around Nanyuki estates or
            neighboring towns, contact our delivery support.
          </p>
        </>
      ),
    },
  };

  const TikTokIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="w-5 h-5">
      <path d="M232 64.5v33.3a93.7 93.7 0 0 1-53.4-16.5v78.7a80 80 0 1 1-80-80 77.3 77.3 0 0 1 16 1.7v34.2a46.2 46.2 0 1 0 32 44.1V24h32a62.1 62.1 0 0 0 53.4 40.5Z" />
    </svg>
  );

  return (
    <>
      <footer className="bg-black text-white py-16 px-6 mt-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-center sm:text-left">
          {/* Branding */}
          <div className="flex flex-col items-center sm:items-start">
            <h3 className="text-2xl font-bold mb-4 tracking-wide text-yellow-500">
              Pak Fashions
            </h3>
            <p className="text-sm text-gray-400 max-w-xs leading-relaxed text-center sm:text-left">
              Clothes designed with passion, inspired by culture. Made with love in Kenya.
            </p>
          </div>

          {/* Quick Links */}
          <nav>
            <h4 className="text-lg font-semibold mb-5 text-white">Quick Links</h4>
            <ul className="space-y-3 text-sm font-medium text-gray-400">
              {["Home", "Shop", "Blog", "About", "Contact", "FAQs", "Testimonials"].map((page) => (
                <li key={page}>
                  <Link
                    to={`/${page.toLowerCase()}`}
                    className="hover:text-yellow-400 transition-colors inline-block"
                  >
                    {page}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Help & Info (Modal-based) */}
          <div>
            <h4 className="text-lg font-semibold mb-5 text-white">Help & Info</h4>
            <ul className="space-y-3 text-sm font-medium text-gray-400">
              {[
                { key: "shipping", label: "Shipping Information" },
                { key: "returns", label: "Return Policy" },
                { key: "privacy", label: "Privacy Policy" },
                { key: "terms", label: "Terms of Service" },
              ].map(({ key, label }) => (
                <li key={key}>
                  <button
                    onClick={() => setActiveModal(key)}
                    className="hover:text-yellow-400 transition-colors focus:outline-none focus:ring-1 focus:ring-yellow-400 rounded-sm"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Community */}
          <div>
            <h4 className="text-lg font-semibold mb-5 text-white">Contact Us</h4>
            <div className="text-sm text-gray-400 space-y-2 mb-6">
              <p>Email: pakfashionske@gmail.com</p>
              <p>Phone: +254 724 835785</p>
              <p>Alt: +254 720 146386</p>
              <p>Hours: Mon–Sun, 7am–9pm</p>
            </div>
            <div className="flex justify-center sm:justify-start gap-4 mb-6">
              {[
                { href: "https://web.facebook.com/PAKFASHIONSKE", icon: <Facebook size={18} />, label: "Facebook" },
                { href: "https://www.instagram.com/pakfashionske?igsh=MXZsbXd3YnRhamltYg==", icon: <Instagram size={18} />, label: "Instagram" },
                { href: "https://www.tiktok.com/@pak.fashions?_t=ZM-8yA1gcZDqD8&_r=1", icon: <TikTokIcon />, label: "TikTok" },
              ].map(({ href, icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-yellow-500 hover:text-black transition-colors text-white"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>
        </div>
        
        {/* Newsletter Section */}
        <div className="max-w-6xl mx-auto mt-12 py-10 border-t border-b border-gray-800 text-center">
          <p className="text-xl font-bold text-white mb-6">Join Our Community</p>
          <form
            className="flex flex-col sm:flex-row gap-3 justify-center max-w-lg mx-auto"
            onSubmit={handleSubscribe}
          >
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-grow px-5 py-3 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-yellow-400"
              required
            />
            <button
              type="submit"
              className="bg-yellow-500 text-black font-semibold px-8 py-3 rounded-lg hover:bg-yellow-600 transition-colors focus:ring-2 focus:ring-yellow-500 focus:outline-none"
            >
              Subscribe
            </button>
          </form>
        </div>

        <div className="mt-8 text-sm text-gray-500 text-center">
          &copy; {new Date().getFullYear()} Pak Fashions. All rights reserved.
        </div>
      </footer>

      {/* Modal Display */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[70] px-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 relative shadow-2xl animate-in zoom-in-95 duration-200">
            <button
              onClick={closeModal}
              className="absolute top-4 right-5 text-gray-400 hover:text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-200 rounded-full p-1"
              aria-label="Close modal"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-6 text-gray-900 border-b pb-3">
              {modalContent[activeModal].title}
            </h2>
            <div className="text-gray-700 text-base leading-relaxed overflow-y-auto max-h-[60vh] pr-2">
              {modalContent[activeModal].content}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Footer;
